import React from 'react';
import {
  isTransparent,
  isValidColor,
  parseColor,
  isPartiallyTransparent,
} from '@packages/systems/core/utils/ColorUtils';

import {designerTheme, emotionStyled} from '@packages/utilities/styled';

import * as UiSwatch from './UiSwatch';

// @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
const isDefaultColors = (id) =>
  id === 'black' || id === 'white' || id === 'transparent';

// @ts-expect-error - TS7031 - Binding element 'colorIsTransparent' implicitly has an 'any' type. | TS7031 - Binding element 'swatchId' implicitly has an 'any' type. | TS7031 - Binding element 'iconStyle' implicitly has an 'any' type. | TS7031 - Binding element 'color' implicitly has an 'any' type.
const Indicator = ({colorIsTransparent, swatchId, iconStyle, color}) => {
  if (!color) {
    return <UiSwatch.Droplet />;
  }

  if (colorIsTransparent || isPartiallyTransparent(color)) {
    return (
      <>
        <UiSwatch.DarkTransparent style={iconStyle} />
        {swatchId && <UiSwatch.Nib style={iconStyle} />}
      </>
    );
  }

  if (!swatchId || isDefaultColors(swatchId)) {
    return null;
  }

  return <UiSwatch.Nib style={iconStyle} />;
};

const Backdrop = emotionStyled('div')(
  {
    background: '#d9d9d9',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  {
    variants: {
      isColorTransparent: {
        true: {
          // stops the swatch backdrop background showing through the corners when color is transparent
          borderTopLeftRadius: 1,
          borderBottomLeftRadius: 1,
        },
      },
    },
  }
);

const SwatchView = emotionStyled(UiSwatch.Swatch)({
  borderTopColor: 'transparent !important',
  borderLeftColor: 'transparent !important',
  borderBottomColor: 'transparent !important',
});

interface SwatchProps {
  color: string;
  id?: string;
  style: React.CSSProperties;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: (arg1: React.MouseEvent<HTMLDivElement>) => void;
}

const Swatch = React.forwardRef<HTMLDivElement, SwatchProps>(
  ({color, style, id, ...rest}, ref) => {
    const colorIsTransparent =
      !isValidColor(color) || id === 'transparent' || isTransparent(color);
    let background;

    if (!color) {
      background = designerTheme.colors.foreground;
    } else if (colorIsTransparent) {
      background = designerTheme.colors.foreground;
    } else {
      background = color;
    }

    const iconStyle = {
      width: style.width,
      height: style.height,
    } as const;

    return (
      // @ts-expect-error [PDTY-2706] - TS2322 - Type '{ children: (false | Element)[]; "data-automation-id": string; style: any; color: any; }' is not assignable to type 'IntrinsicAttributes & ((Diff<{ theme: { control: any; colors: any; }; style: { [x: string]: any; background: any; }; }, { style?: StyleObject; }> & RefAttributes<...>) | (Omit<...> & RefAttributes<...>))'.
      <SwatchView
        data-automation-id="swatch"
        style={{
          ...style,
          color: colorIsTransparent
            ? designerTheme.colors.backgroundDark
            : isPartiallyTransparent(color)
              ? designerTheme.colors.foreground
              : 'currentColor',
        }}
        color={color}
        {...rest}
        ref={ref}
      >
        <Backdrop
          isColorTransparent={
            colorIsTransparent || isPartiallyTransparent(color)
          }
        />
        {!colorIsTransparent && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'Background<string | number> | undefined'.
              background: parseColor(background),
            }}
          />
        )}
        <Indicator
          colorIsTransparent={colorIsTransparent}
          iconStyle={iconStyle}
          swatchId={id}
          color={color}
        />
      </SwatchView>
    );
  }
);

Swatch.displayName = 'Swatch';

// Missing type annotation for A.
// A is a type parameter declared in function type and was implicitly instantiated at call of onlyUpdateForKeys
export default React.memo(Swatch);
